<template>
    <div class="gauge-chart-container">
        <div class="gauge-chart" ref="gaugeChart" />
    </div>
</template>

<script>
import * as echarts from "echarts";

export default {
    props: {
        needleData: {},
        maxData: {},
        unit: {},
        color: {},
        size: {},
        showInfo: {},
        width: {},
        height: {},
        peakData: {},
        compact: { type: Boolean, default: false },
    },
    data() {
        return {
            chart: null,
            standardWidth: 5, // 적절한 표준 크기
            standardHeight: 5, // 적절한 표준 크기
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.renderChart();
        });
        window.addEventListener("resize", this.resizeChart);
    },
    watch: {
        needleData: {
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
            deep: true,
        },
        maxData: {
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
            deep: true,
        },
        showInfo: {
            handler() {
                if (this.chart) {
                    this.renderChart();
                }
            },
        },
        width: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.renderChart();
                    }
                });
            },
        },
        height: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.renderChart();
                    }
                });
            },
        },
        compact: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.renderChart();
                    }
                });
            },
        },
        shouldShowText: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.$nextTick(() => {
                        if (this.chart) {
                            this.renderChart();
                        }
                    });
                }
            },
        },
    },
    computed: {
        gaugeChartSize() {
            // 개요형 compact: 셀 안으로 강제 (작을수록 키우던 PC 로직 차단)
            if (this.compact) {
                return this.showInfo ? "90%" : "95%";
            }
            // showInfo가 true인 경우
            if (this.showInfo) {
                if (this.width === this.standardWidth) {
                    return "95%";
                } else if (this.width < this.standardWidth) {
                    return "120%";
                } else {
                    return "120%";
                }
            }
            // showInfo가 false인 경우
            else {
                if (this.height === this.standardHeight) {
                    return "100%";
                } else if (this.height < this.standardHeight) {
                    return "130%";
                } else {
                    return "90%";
                }
            }
        },
        gaugeChartCenter() {
            if (this.compact) {
                // 반원 게이지를 셀 중앙~약간 하단에 두어 수치·아크가 보이도록
                return this.showInfo ? ["50%", "68%"] : ["50%", "65%"];
            }
            if (this.showInfo) {
                return ["50%", "80%"];
            } else {
                if (this.height === this.standardHeight) {
                    return ["50%", "80%"];
                } else if (this.height < this.standardHeight) {
                    return ["50%", "85%"];
                } else {
                    return ["50%", "80%"];
                }
            }
        },
        shouldShowText() {
            if (this.compact) return true;
            const result = this.width >= this.standardWidth && this.height >= this.standardHeight;
            return result;
        },
        detailFontSize() {
            return this.compact ? 13 : 16;
        },
        detailOffsetCenter() {
            if (this.compact) {
                return [0, this.showInfo ? "-18%" : "-22%"];
            }
            return [0, this.showInfo ? "-30%" : "-35%"];
        },
    },    beforeDestroy() {
        window.removeEventListener("resize", () => {
            this.chart.resize();
        });
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        resizeChart() {
            if (this.chart) {
                setTimeout(() => {
                    this.chart.resize();
                }, 100);
            }
        },
        renderChart() {
            if (!this.$refs.gaugeChart) {
                return;
            }

            const el = this.$refs.gaugeChart;
            // 컨테이너 높이가 0이면 init해도 빈 캔버스 → 레이아웃 확정 후 재시도
            if (!el.clientWidth || !el.clientHeight) {
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        if (this.$refs.gaugeChart?.clientWidth && this.$refs.gaugeChart?.clientHeight) {
                            this.renderChart();
                        }
                    });
                });
                return;
            }

            if (!this.chart) {
                this.chart = echarts.init(el);
            }
            let unit = this.unit || "";
            let needleValue = Number(this.needleData);
            if (!isFinite(needleValue)) needleValue = 0;
            const maxRaw = this.maxData == null ? "0" : String(this.maxData);
            let maxData = Number(maxRaw.replace(/,/g, ""));
            if (!isFinite(maxData) || maxData < 0) maxData = 0;
            let percentage = maxData === 0 ? 0 : ((100 * needleValue) / maxData).toFixed(0);

            // 색상에 따른 6단계 색상 배열 정의
            const getColorArray = (color) => {
                const colorSets = {
                    blue: [
                        [0.2, "#E1F5FE"],
                        [0.4, "#81D4FA"],
                        [0.6, "#29B6F6"],
                        [0.8, "#0288D1"],
                        [1.0, "#01579B"],
                    ],
                    charcoal: [
                        [0.2, "#ECEFF1"],
                        [0.4, "#B0BEC5"],
                        [0.6, "#78909C"],
                        [0.8, "#455A64"],
                        [1.0, "#263238"],
                    ],
                    red: [
                        [0.2, "#FFEBEE"],
                        [0.4, "#EF9A9A"],
                        [0.6, "#EF5350"],
                        [0.8, "#D32F2F"],
                        [1.0, "#B71C1C"],
                    ],
                    green: [
                        [0.2, "#F1F8E9"],
                        [0.4, "#C5E1A5"],
                        [0.6, "#9CCC65"],
                        [0.8, "#689F38"],
                        [1.0, "#33691E"],
                    ],
                };
                return colorSets[color] || colorSets.blue;
            };

            // 베이스 색상에 따른 포인터 색상 정의
            const getPointerColor = (color) => {
                const pointerColors = {
                    blue: "#01579B",
                    charcoal: "#263238",
                    red: "#B71C1C",
                    green: "#33691E",
                };
                return pointerColors[color] || pointerColors.charcoal;
            };

            const option = {
                series: [
                    {
                        type: "gauge",
                        startAngle: 180,
                        endAngle: 0,
                        center: this.gaugeChartCenter,
                        radius: this.gaugeChartSize,
                        min: 0,
                        max: 100,
                        splitNumber: 10,
                        axisLine: {
                            lineStyle: {
                                width: 8,
                                color: getColorArray(this.color),
                            },
                        },
                        pointer: {
                            icon: "path://M2090.36389,615.30999 L2090.36389,615.30999 C2091.48372,615.30999 2092.40383,616.194028 2092.44859,617.312956 L2096.90698,728.755929 C2097.05155,732.369577 2094.2393,735.416212 2090.62566,735.56078 C2090.53845,735.564269 2090.45117,735.566014 2090.36389,735.566014 L2090.36389,735.566014 C2086.74736,735.566014 2083.81557,732.63423 2083.81557,729.017692 C2083.81557,728.930412 2083.81732,728.84314 2083.82081,728.755929 L2088.2792,617.312956 C2088.32396,616.194028 2089.24407,615.30999 2090.36389,615.30999 Z",
                            length: this.compact ? "22%" : "30%",
                            width: this.compact ? 10 : 13,
                            offsetCenter: this.compact
                                ? [0, this.showInfo ? "-42%" : "-55%"]
                                : this.showInfo
                                  ? [0, "-50%"]
                                  : [0, "-70%"],
                            itemStyle: {
                                color: getPointerColor(this.color),
                                shadowBlur: 10,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                                shadowOffsetX: 2,
                                shadowOffsetY: 2,
                            },
                        },
                        axisTick: {
                            length: this.compact ? 3 : 4,
                            distance: 1,
                            lineStyle: {
                                color: "#90A4AE",
                                width: 1,
                            },
                        },
                        splitLine: {
                            length: this.compact ? 6 : 8,
                            distance: 1,
                            lineStyle: {
                                color: "#90A4AE",
                                width: 2,
                            },
                        },
                        axisLabel: {
                            show: false,
                            color: "#464646",
                            fontSize: 10,
                            distance: -20,
                            rotate: "tangential",
                        },
                        title: {
                            offsetCenter: [0, "-10%"],
                            fontSize: this.compact ? 11 : 14,
                        },
                        detail: {
                            fontSize: this.detailFontSize,
                            offsetCenter: this.detailOffsetCenter,
                            valueAnimation: true,
                            color: "black",
                            formatter: () => {
                                return `${needleValue}${unit}`;
                            },
                        },
                        data: [{ value: percentage, name: this.showInfo ? "현재" : "" }],
                    },
                ],
            };

            if (!this.showInfo && this.peakData) {
                option.graphic = [
                    {
                        type: "text",
                        left: "center",
                        top: "73%",
                        show: !this.showInfo && this.shouldShowText,
                        style: {
                            text: `계약전력: ${Number(this.peakData.contractPeak[0]).toFixed(0) || 0} ${this.peakData.contractPeak[1]}`,
                            fontSize: 10,
                            fill: "#666",
                            fontWeight: "normal",
                            textAlign: "center",
                        },
                    },
                    {
                        type: "text",
                        left: "center",
                        top: "83%",
                        show: !this.showInfo && this.shouldShowText,
                        style: {
                            text: `금일피크: ${Number(this.peakData.maxPeak[0]).toFixed(0) || 0} ${this.peakData.maxPeak[1]}`,
                            fontSize: 10,
                            fill: "#666",
                            fontWeight: "normal",
                            textAlign: "center",
                        },
                    },
                ];
            }

            // 기존 옵션을 완전히 초기화하고 새 옵션 설정
            this.chart.clear();
            this.chart.setOption(option, true);

            // 레이아웃 확정 후 리사이즈 (compact 그리드 전환 대응)
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    if (this.chart) {
                        this.chart.resize();
                    }
                });
            });
        },
    },
};
</script>

<style scoped>
.gauge-chart-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}
.gauge-chart {
    width: 100%;
    height: 100%;
}
</style>
